(function (root) {
  'use strict';
  function buildSnapshot(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
    const grouped = new Map();
    let count = 0;
    for (const item of cart.items) {
      const id = String(item.variant_id || '');
      if (!/^[1-9][0-9]*$/.test(id) || !Number.isSafeInteger(Number(id))) throw new Error('Invalid variant ID');
      if (!Number.isSafeInteger(item.quantity) || item.quantity < 1) throw new Error('Invalid quantity');
      if (item.selling_plan_allocation || Object.keys(item.properties || {}).length) throw new Error('Cart requires unsupported line options');
      grouped.set(id, (grouped.get(id) || 0) + item.quantity);
      count += item.quantity;
    }
    if (!Number.isSafeInteger(count) || count !== cart.item_count) throw new Error('Incomplete cart response');
    const items = Array.from(grouped, ([VariantID, Quantity]) => ({ VariantID, Quantity })).sort((a, b) => a.VariantID.localeCompare(b.VariantID));
    const pairs = items.map(item => item.VariantID + ':' + item.Quantity).join(',');
    const url = pairs ? 'https://mostlyfitclub.com/cart/' + pairs + '?storefront=true' : '';
    if (url.length > 1900) throw new Error('Cart permalink exceeds safe length');
    return { CartItems: items, ItemCount: count, CartRestorable: count > 0, CartRestoreURL: url, CartRestoreDiscountURL: url ? url + '&discount=CART10' : '', SchemaVersion: 1 };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { buildSnapshot };
  if (!root || !root.document || root.MFCCartTracking) return;
  const doc = root.document;
  const nativeFetch = root.fetch.bind(root);
  const state = { status: 'starting', snapshot: null, error: null };
  let chain = Promise.resolve();
  let lastSent = '';
  let timer;
  let pendingAdd = false;
  let pendingVariant;
  let readyAttempts = 0;
  function report(status, error) {
    state.status = status;
    state.error = error || null;
    doc.dispatchEvent(new CustomEvent('mfc:cart-tracking-status', { detail: { status, error: state.error, snapshot: state.snapshot } }));
  }
  function allowed() {
    const privacy = root.Shopify && root.Shopify.customerPrivacy;
    return !!privacy && privacy.analyticsProcessingAllowed() && privacy.marketingAllowed();
  }
  function sdkCall(name, ...args) {
    return Promise.race([
      Promise.resolve().then(() => root.klaviyo[name](...args)),
      new Promise((_, reject) => root.setTimeout(() => reject(new Error('Klaviyo timeout')), 7000))
    ]);
  }
  async function synchronize(addition, variantId) {
    if (!allowed()) { report('consent-not-allowed'); return; }
    if (!root.klaviyo || !(await sdkCall('isIdentified'))) { report('waiting-for-identification'); return; }
    const route = (root.Shopify.routes && root.Shopify.routes.root) || '/';
    const response = await nativeFetch(route + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart read failed');
    const cart = await response.json();
    let snapshot;
    try { snapshot = buildSnapshot(cart); }
    catch (error) {
      await sdkCall('identify', { MFC_CartRestorable: false, MFC_CartRestoreURL: '', MFC_CartRestoreDiscountURL: '', MFC_CartItems: [] });
      throw error;
    }
    if (!allowed()) { report('consent-not-allowed'); return; }
    await sdkCall('identify', {
      MFC_CartRestorable: snapshot.CartRestorable,
      MFC_CartRestoreURL: snapshot.CartRestoreURL,
      MFC_CartRestoreDiscountURL: snapshot.CartRestoreDiscountURL,
      MFC_CartItems: snapshot.CartItems,
      MFC_CartItemCount: snapshot.ItemCount,
      MFC_CartUpdatedAt: new Date().toISOString()
    });
    state.snapshot = snapshot;
    const fingerprint = JSON.stringify(snapshot.CartItems);
    if (addition && snapshot.CartRestorable && fingerprint !== lastSent) {
      const item = cart.items.find(item => String(item.variant_id) === String(variantId)) || cart.items[cart.items.length - 1];
      const tracked = await sdkCall('track', 'MFC Full Cart Added', {
        ...snapshot,
        '$value': cart.total_price / 100,
        '$currency': cart.currency,
        'Product Name': item.product_title || item.title,
        ProductID: String(item.product_id),
        VariantID: String(item.variant_id),
        Quantity: item.quantity,
        'Variant Name': item.variant_title,
        ImageURL: item.image || '',
        URL: new URL(item.url, 'https://mostlyfitclub.com').href,
        Price: (item.final_price === undefined ? item.price : item.final_price) / 100
      });
      if (tracked === false) throw new Error('Klaviyo rejected event');
      lastSent = fingerprint;
    }
    report('synced');
  }
  function refresh(addition, variantId) {
    chain = chain.catch(() => {}).then(() => synchronize(!!addition, variantId)).catch(error => report('error', error.message));
    return chain;
  }
  function schedule(addition, variantId) {
    pendingAdd = pendingAdd || !!addition;
    pendingVariant = variantId || pendingVariant;
    root.clearTimeout(timer);
    timer = root.setTimeout(() => {
      const add = pendingAdd;
      const variant = pendingVariant;
      pendingAdd = false;
      pendingVariant = undefined;
      refresh(add, variant);
    }, 400);
  }
  root.fetch = function (input, options) {
    let mutation = null;
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, root.location.href);
      const method = ((options && options.method) || (input && input.method) || 'GET').toUpperCase();
      if (url.origin === root.location.origin && method === 'POST') mutation = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/);
    } catch (_) {}
    return nativeFetch(input, options).then(response => {
      if (mutation && response.ok) schedule(mutation[1] === 'add');
      return response;
    });
  };
  function start() {
    if (!root.Shopify || !root.klaviyo || (!root.Shopify.customerPrivacy && !root.Shopify.loadFeatures) || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      if (++readyAttempts < 30) root.setTimeout(start, 1000);
      else report('error', 'Storefront tracking dependencies unavailable');
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      schedule(event.source === 'product-form' || event.source === 'quick-add', event.productVariantId || event.variantId);
    });
    doc.addEventListener('visitorConsentCollected', () => schedule(false));
    root.addEventListener('pageshow', () => schedule(false));
    doc.addEventListener('visibilitychange', () => { if (!doc.hidden) schedule(false); });
    root.addEventListener('klaviyoForms', event => { if (event.detail && event.detail.type === 'submit') schedule(false); });
    if (root.Shopify.customerPrivacy) schedule(false);
    else if (root.Shopify.loadFeatures) root.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
      if (error) report('error', 'Consent API unavailable');
      else schedule(false);
    });
    else report('error', 'Consent API unavailable');
  }
  root.MFCCartTracking = { refresh, state, buildSnapshot };
  start();
})(typeof window === 'undefined' ? null : window);
